/**
 * Čo urobí nahratie cenníka — kým sa to nestalo (KOLO 165).
 *
 * Server spustil ten istý import ako potvrdenie a vrátil ho späť, takže
 * okno ukazuje presne to, čo „Potvrdiť import" zapíše: čo **pribudne**,
 * čomu sa **zmení cena** a čo z cenníka **zmizne**. Pri zmiznutých platí
 * rozhodnutie usera: nepoužitá v zákazke sa vyradí, použitá ostane so
 * štítkom „nie je v cenníku“ — preto má každá v tabuľke napísané, čo
 * sa s ňou stane.
 *
 * Počty sú vždy celé; tabuľky len ukážky (server pošle prvých 200,
 * zmiznutých 500).
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ZdrojNahlad } from "@/api/hooks/useCennik";

/** Nákupná cena — hrany stoja centy, dve desatinné miesta by zmenu skryli. */
function cena(v: string | number, mj: string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  const s = n.toLocaleString("sk-SK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
  return `${s} €/${mj}`;
}

function percento(v: string | number | null | undefined): string {
  if (v == null) return "nová cena";
  const n = Number(v);
  const s = n.toLocaleString("sk-SK", { maximumFractionDigits: 1 });
  return n > 0 ? `+${s} %` : `${s} %`;
}

function Ukazka({ zobrazene, spolu }: { zobrazene: number; spolu: number }): JSX.Element | null {
  if (zobrazene >= spolu) return null;
  return (
    <p className="text-xs text-muted-foreground">
      Zobrazených prvých {zobrazene} z {spolu}.
    </p>
  );
}

export function ImportNahladDialog({
  open,
  label,
  nahlad,
  potvrdzuje,
  onPotvrdit,
  onZrusit,
}: {
  open: boolean;
  /** Názov cenníka (`Häfele`, `Hranipex — ABS hrany`). */
  label: string;
  nahlad: ZdrojNahlad | null;
  /** Potvrdenie beží — import celého cenníka trvá. */
  potvrdzuje: boolean;
  onPotvrdit: () => void;
  onZrusit: () => void;
}): JSX.Element {
  const n = nahlad;
  const zmenenych = n ? n.zdrazenych + n.zlacnenych : 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !potvrdzuje) onZrusit();
      }}
    >
      <DialogContent className="flex max-h-[85vh] w-[96vw] max-w-4xl flex-col">
        <DialogHeader>
          <DialogTitle>Náhľad — {label}</DialogTitle>
          <DialogDescription>
            Nič sa zatiaľ nezapísalo. Skontroluj zmeny a potvrď import.
          </DialogDescription>
        </DialogHeader>

        {n ? (
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pr-1">
            <ul className="space-y-1 text-sm" data-testid="nahlad-suhrn">
              <li>
                Položiek v súbore: <b>{n.spolu}</b>
              </li>
              <li>
                Pribudne: <b>{n.novych}</b>
              </li>
              <li>
                Zmení sa cena: <b>{zmenenych}</b>
                {zmenenych > 0 ? (
                  <span className="text-muted-foreground">
                    {" "}
                    (zdražie {n.zdrazenych}, zlacnie {n.zlacnenych}
                    {n.priemer_zmeny_pct != null
                      ? `, priemerne ${percento(n.priemer_zmeny_pct)}`
                      : ""}
                    )
                  </span>
                ) : null}
              </li>
              <li>
                Zmizne z cenníka: <b>{n.chybajucich}</b>
                {n.chybajucich > 0 ? (
                  <span className="text-muted-foreground">
                    {" "}
                    — {n.vyradi_sa} sa vyradí, {n.ostane_so_stitkom} ostane so
                    štítkom „nie je v cenníku“ (používa sa v zákazke)
                  </span>
                ) : null}
              </li>
              {n.obnovenych > 0 ? (
                <li>
                  Vráti sa do cenníka: <b>{n.obnovenych}</b>
                </li>
              ) : null}
              {n.sety_prepocitane ? (
                <li>
                  Sety kovania s novou cenou zo súčtu komponentov:{" "}
                  <b>{n.sety_prepocitane}</b>
                </li>
              ) : null}
              {n.prepocet_zakaziek ? (
                <li>
                  Balenie → kus: v <b>{n.prepocet_zakaziek}</b> otvorených
                  zákazkách sa počet {n.prepocet_riadkov} riadkov vynásobí
                  kusmi v balení (položiek: {n.prepocet_poloziek}). Fakturované,
                  archivované a tie, ktoré už zmenili stav, sa nemenia.
                </li>
              ) : null}
              {n.zrusena_uprava_percent != null ? (
                <li>
                  Úprava cien {percento(n.zrusena_uprava_percent)} sa zruší pre
                  tento cenník.
                </li>
              ) : null}
              {n.errors.length > 0 ? (
                <li className="text-destructive">
                  Riadky so chybou: {n.errors.length} (napr. {n.errors[0]})
                </li>
              ) : null}
            </ul>

            {n.nove.length > 0 ? (
              <section className="space-y-1" data-testid="nahlad-pribudne">
                <h3 className="text-sm font-medium">
                  Pribudne <span className="text-muted-foreground">({n.novych})</span>
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[160px]">Kód</TableHead>
                      <TableHead>Názov</TableHead>
                      <TableHead className="w-[140px] text-right">Cena</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {n.nove.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-mono text-xs">{p.kod ?? "—"}</TableCell>
                        <TableCell className="text-xs">{p.nazov}</TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {cena(p.nakup_cena, p.mj)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Ukazka zobrazene={n.nove.length} spolu={n.novych} />
              </section>
            ) : null}

            {n.zmeny_cien.length > 0 ? (
              <section className="space-y-1" data-testid="nahlad-ceny">
                <h3 className="text-sm font-medium">
                  Zmení sa cena{" "}
                  <span className="text-muted-foreground">({zmenenych})</span>
                </h3>
                <p className="text-xs text-muted-foreground">
                  Najväčšie zmeny navrch. Uložené zákazky si ceny prepočítajú
                  pri ďalšom otvorení.
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[160px]">Kód</TableHead>
                      <TableHead>Názov</TableHead>
                      <TableHead className="w-[130px] text-right">Teraz</TableHead>
                      <TableHead className="w-[130px] text-right">Nová</TableHead>
                      <TableHead className="w-[90px] text-right">Zmena</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {n.zmeny_cien.map((z) => (
                      <TableRow key={z.id}>
                        <TableCell className="font-mono text-xs">{z.kod ?? "—"}</TableCell>
                        <TableCell className="text-xs">{z.nazov}</TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {/* Balenie prepočítané na kus (KOLO 165) — stará
                              cena je za balenie, nie za kus */}
                          {cena(z.stara, z.mj_pred ?? z.mj)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {cena(z.nova, z.mj)}
                        </TableCell>
                        <TableCell
                          className={
                            Number(z.nova) > Number(z.stara)
                              ? "text-right font-mono text-xs text-destructive"
                              : "text-right font-mono text-xs text-emerald-700 dark:text-emerald-400"
                          }
                        >
                          {percento(z.rozdiel_pct)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Ukazka zobrazene={n.zmeny_cien.length} spolu={zmenenych} />
              </section>
            ) : null}

            {n.chybajuce.length > 0 ? (
              <section className="space-y-1" data-testid="nahlad-zmizne">
                <h3 className="text-sm font-medium">
                  Zmizne z cenníka{" "}
                  <span className="text-muted-foreground">({n.chybajucich})</span>
                </h3>
                <p className="text-xs text-muted-foreground">
                  V novom súbore nie sú. Nepoužité sa vyradia (keď sa do
                  cenníka vrátia, import ich znova zapne); použité v zákazke
                  ostanú so štítkom „nie je v cenníku“ a zákazka sa nezmení.
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[160px]">Kód</TableHead>
                      <TableHead>Názov</TableHead>
                      <TableHead className="w-[130px] text-right">Cena</TableHead>
                      <TableHead className="w-[170px]">Čo sa stane</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {n.chybajuce.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="font-mono text-xs">{c.kod ?? "—"}</TableCell>
                        <TableCell className="text-xs">{c.nazov}</TableCell>
                        <TableCell className="text-right font-mono text-xs">
                          {cena(c.nakup_cena, c.mj)}
                        </TableCell>
                        <TableCell className="text-xs">
                          {c.pouziva_sa ? (
                            <span className="rounded bg-amber-100 px-1 py-0.5 text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                              ostane — je v zákazke
                            </span>
                          ) : (
                            <span className="text-muted-foreground">vyradí sa</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Ukazka zobrazene={n.chybajuce.length} spolu={n.chybajucich} />
              </section>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onZrusit} disabled={potvrdzuje}>
            Zrušiť
          </Button>
          <Button onClick={onPotvrdit} disabled={!n || potvrdzuje}>
            {potvrdzuje ? "Importuje sa…" : "Potvrdiť import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
